import { useState, useMemo } from 'react';
import { Eye, EyeOff, ChevronDown, ChevronUp, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  SECTION_GROUPS,
  ALL_VISIBLE,
  NONE_VISIBLE,
  PRESETS,
  detectPreset,
  hasAnyVisible,
  getAdminHiddenSections,
} from '@/config/sectionVisibility';
import { useReportVisibility } from '../contexts/ReportVisibilityContext';

export const SectionVisibilityControls = ({ visibility, onChange }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { visibility: adminVis, isAdmin } = useReportVisibility();

  // Compute admin-hidden section IDs (non-admin only)
  const adminHidden = useMemo(
    () => isAdmin ? new Set() : getAdminHiddenSections(adminVis),
    [adminVis, isAdmin]
  );

  // Filter SECTION_GROUPS to exclude admin-hidden sections
  const filteredGroups = useMemo(() => {
    if (adminHidden.size === 0) return SECTION_GROUPS;
    return SECTION_GROUPS
      .map(g => ({
        ...g,
        sections: g.sections.filter(s => !adminHidden.has(s.id)),
      }))
      .filter(g => g.sections.length > 0);
  }, [adminHidden]);

  // Build filtered ALL_VISIBLE / NONE_VISIBLE that exclude admin-hidden sections
  const filteredAllVisible = useMemo(() => {
    if (adminHidden.size === 0) return ALL_VISIBLE;
    const state = { ...ALL_VISIBLE };
    adminHidden.forEach(id => { state[id] = false; });
    return state;
  }, [adminHidden]);

  const filteredNoneVisible = useMemo(() => {
    if (adminHidden.size === 0) return NONE_VISIBLE;
    const state = { ...NONE_VISIBLE };
    adminHidden.forEach(id => { state[id] = false; });
    return state;
  }, [adminHidden]);

  // Count only non-hidden visible sections
  const visibleCount = Object.entries(visibility).filter(
    ([id, v]) => v && !adminHidden.has(id)
  ).length;

  const activePreset = detectPreset(visibility);
  const anyVisible = hasAnyVisible(visibility);

  const toggleSection = (id) => {
    onChange({ ...visibility, [id]: !visibility[id] });
  };

  const toggleGroup = (group) => {
    const allOn = group.sections.every(s => visibility[s.id]);
    const next = { ...visibility };
    group.sections.forEach(s => { next[s.id] = !allOn; });
    onChange(next);
  };

  // Filter presets to exclude admin-hidden sections from their state
  const applyPreset = (presetState) => {
    if (!presetState) return;
    if (adminHidden.size === 0) { onChange(presetState); return; }
    const filtered = { ...presetState };
    adminHidden.forEach(id => { filtered[id] = false; });
    onChange(filtered);
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm"
      data-testid="section-visibility-controls"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setCollapsed(!collapsed)}
        data-testid="visibility-header"
      >
        <div className="flex items-center gap-2">
          {anyVisible ? (
            <Eye className="w-4 h-4 text-teal-600" />
          ) : (
            <EyeOff className="w-4 h-4 text-gray-400" />
          )}
          <span className="text-sm font-semibold text-gray-800">Report Sections</span>
          <span className="text-xs text-gray-400">
            ({visibleCount} visible)
          </span>
        </div>
        {collapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        )}
      </div>

      {!collapsed && (
        <div className="px-3 pb-3 space-y-3">
          {/* Presets row */}
          <div className="flex flex-wrap items-center gap-2" data-testid="preset-buttons">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => p.state && applyPreset(p.state)}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                  activePreset === p.id
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400 hover:text-teal-700'
                }`}
                data-testid={`preset-${p.id}`}
              >
                {p.label}
              </button>
            ))}

            <span className="mx-1 h-4 w-px bg-gray-200" />

            <button
              onClick={() => onChange(filteredAllVisible)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-teal-700 transition-colors"
              data-testid="select-all-btn"
            >
              <CheckSquare className="w-3 h-3" />
              Select All
            </button>
            <button
              onClick={() => onChange(filteredNoneVisible)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-red-600 transition-colors"
              data-testid="clear-all-btn"
            >
              <Square className="w-3 h-3" />
              Clear All
            </button>
          </div>

          {/* Grouped checkboxes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredGroups.map((group) => {
              const allOn = group.sections.every(s => visibility[s.id]);
              const someOn = group.sections.some(s => visibility[s.id]);
              return (
                <div
                  key={group.id}
                  className="rounded-lg border border-slate-100 bg-white p-3 shadow-sm"
                  data-testid={`group-${group.id}`}
                >
                  {/* Group header with toggle */}
                  <label className="flex items-center gap-2 cursor-pointer mb-2 pb-1.5 border-b border-slate-100">
                    <input
                      type="checkbox"
                      checked={allOn}
                      ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                      onChange={() => toggleGroup(group)}
                      className="w-3.5 h-3.5 rounded"
                      data-testid={`group-toggle-${group.id}`}
                    />
                    <span className={`text-xs font-bold ${group.color}`}>{group.label}</span>
                  </label>

                  {/* Section checkboxes */}
                  <div className="space-y-1.5 ml-1">
                    {group.sections.map((sec) => (
                      <label
                        key={sec.id}
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                      >
                        <input
                          type="checkbox"
                          checked={!!visibility[sec.id]}
                          onChange={() => toggleSection(sec.id)}
                          className="w-3 h-3 rounded"
                          data-testid={`section-toggle-${sec.id}`}
                        />
                        <span className="text-xs text-slate-600">{sec.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
